import React from 'react';
import { View, ScrollView } from 'react-native';

import { isFunction as _isFunction, map as _map } from 'lodash';
import PropTypes from 'prop-types';

import { ErrorScreen, UsageItem, VFScreen } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { getPlaceholdersBasedOnUsageType, getUsageProperColor } from '../../../Utils/Getter';
import ChartUsageCard from '../../Components/ChartUsageCard/ChartUsageCard';
import UsageLoading from '../../Components/UsageLoading/UsageLoading';

import styles from './UsageUserGraphs.Style';

function UsageUserGraphs({
  usage,
  isLoading,
  error,
  vfScreenProps,
  images,
  title,
  onClose,
  withTray,
  isIndividual,
  onTryAgainPress,
  ...props
}) {
  const { user, userUsageInfo } = usage;
  const theme = useTheme();
  const renderGraphs = () => {
    return _map(userUsageInfo, usageItem => {
      const percentageColor = getUsageProperColor(usageItem.overview.type);

      // destructing data from the overview to display it in UsageItem
      const { type, icon, isUnlimited, valuePercentage, remaining, total } = usageItem?.overview;
      // renderFooter function to use extra component on the ChartUsageComponent
      const renderFooter = () => {
        return (
          <>
            <View style={styles.separator(theme)} />

            <UsageItem
              percentageIndicatorSecondaryColor={percentageColor}
              percentageIndicatorContainerStyle={styles.percentageIndicatorContainerStyle}
              usageItemLabel={type}
              icon={icon}
              isUnlimitedUsage={isUnlimited}
              usageValuesTextStyle={{ color: percentageColor }}
              usageValuesPlaceholders={getPlaceholdersBasedOnUsageType({
                usageType: type,
                amount: remaining?.value,
                units: remaining?.valueType,
                totalAmount: total?.value,
                totalUnits: total?.valueType,
              })}
              usageValuesText={'soho_usage_usage_card_consumption'}
              unlimitedUsageText="soho_usage_usage_card_unlimited_consumption"
              usagePercentageValue={valuePercentage}
              shouldShowAddUsageButton={false}
              {...usageItem?.overview}
              images={images}
            />
          </>
        );
      };

      const cardTitle = isIndividual ? type : user?.name;

      return (
        <ChartUsageCard
          {...usageItem}
          title={cardTitle}
          containerStyle={styles.chartUsageCardContainerStyle}
          renderFooter={renderFooter}
        />
      );
    });
  };

  const renderLoading = () => {
    return (
      <UsageLoading
        withPercentageView={false}
        withHeaders={false}
        withTabs={false}
        withGraphViewFooter={true}
      />
    );
  };

  const renderError = () => {
    return (
      <ErrorScreen
        backgroundStyle={styles.errorScreenBackgroundStyle}
        iconStyle={styles.iconStyle}
        text={error.message}
        onPress={() => {
          _isFunction(onTryAgainPress) && onTryAgainPress();
        }}
        {...error}
      />
    );
  };

  // render function for the component
  const renderScreen = () => {
    return (
      <ScrollView
        scrollEnabled={!isLoading}
        contentContainerStyle={styles.scrollViewContentContainer}
      >
        <View style={[styles.containerStyle, withTray && styles.containerStyleWithTray]}>
          {isLoading ? renderLoading() : error ? renderError() : renderGraphs()}
        </View>
      </ScrollView>
    );
  };

  return (
    <VFScreen title={title} onClose={onClose} {...vfScreenProps}>
      {renderScreen()}
    </VFScreen>
  );
}

UsageUserGraphs.defaultProps = {
  usage: {},
  isIndividual: false,
};

UsageUserGraphs.propTypes = {
  isIndividual: PropTypes.bool,
  usage: PropTypes.shape({
    user: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      role: PropTypes.string,
      type: PropTypes.string,
    }),
    userUsageInfo: PropTypes.arrayOf(
      PropTypes.shape({
        overview: PropTypes.shape({
          description: PropTypes.string,
          type: PropTypes.string,
          status: PropTypes.string,
          period: PropTypes.string,
          icon: PropTypes.string,
          endDate: PropTypes.string,
          startDate: PropTypes.string,
          usage: PropTypes.shape({
            valueType: PropTypes.string,
            value: PropTypes.number,
          }),
          isUnlimited: false,
          total: PropTypes.shape({
            valueType: PropTypes.string,
            value: PropTypes.number,
          }),
          totalUsage: PropTypes.shape({
            valueType: PropTypes.string,
            value: PropTypes.number,
          }),
          remaining: PropTypes.shape({
            valueType: PropTypes.string,
            value: PropTypes.number,
          }),
          valuePercentage: PropTypes.string,
        }),
        usage: PropTypes.arrayOf(
          PropTypes.shape({
            period: PropTypes.shape({
              usageDuration: PropTypes.string,
              usageInterval: PropTypes.string,
            }),
            usage: PropTypes.arrayOf(
              PropTypes.shape({
                startDate: PropTypes.string,
                endDate: PropTypes.string,
                usage: {
                  value: PropTypes.number,
                  valueType: PropTypes.string,
                },
              }),
            ),
          }),
        ),
      }),
    ),
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  onTryAgainPress: PropTypes.func,
  onClose: PropTypes.func,
  images: PropTypes.object,
  vfScreenProps: PropTypes.object,
  title: PropTypes.string,
  withTray: PropTypes.bool,
};

export default UsageUserGraphs;
